import React, { useEffect, useRef, useState } from 'react';
import {
  CompTitle,
  EpisodeItem,
  PlayerControlButton,
  ScrollableList,
  SeasonItem,
  SeasonTitleTextWrapper,
  SeasonTitleWrapper, SeriesBadge, SeriesIconWrapper, SeriesImage, SeriesImageWrapper,
} from '../styles';
import { __t } from '../../../i18n';
import Panel from '../styles/Panel';
import { Divider, Flex, Icon, Text, Box } from '../../../styles';
import IconRightArrow from '../../../icons/IconRightArrow';
import { IconLeftArrow, IconPause, IconPlayBig, IconSeasons } from '../../../icons';
import { timeToMinuets } from '../../../utils';
import { Season } from '../PlayerModels';
import { useRouter } from 'next/router';
import theme from '../../../theme';
import UsePlayerProps from '../../../Store/usePlayerProps';
import styled from '@emotion/styled';

const SeasonsAndEpisodes = ({ emitActivePanel }: { emitActivePanel: (status: boolean) => void }) => {
  const contentData = UsePlayerProps((state) => state.playerState.contentData);
  const setPlayerGlobalState = UsePlayerProps((state) => state.setPlayerState);
  const router = useRouter();
  const [selectedSeason, setSelectedSeason] = useState<Season | undefined>(undefined);
  const [selectedEpisode, setSelectedEpisode] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredEpisode, setHoveredEpisode] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const currentSeason = contentData?.seasons.find((season) => season.id === contentData?.content.season);

  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    handleMobileBreakpoint();
    window.addEventListener('resize', throttledHandle, { passive: true });
    return () => {
      window.removeEventListener('resize', throttledHandle);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        resizeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedEpisode(contentData?.content.episode as number);
    currentSeason && setSelectedSeason(currentSeason);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    emitActivePanel(isOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const throttledHandle = () => {
    if (resizeTimerRef.current !== null) return;
    resizeTimerRef.current = setTimeout(() => {
      handleMobileBreakpoint();
      resizeTimerRef.current = null;
    }, 100);
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <PlayerControlButton
        size="semiLarge"
        active={isOpen}
        icon={<IconSeasons />}
        mr={isMobile ? 0.75 : 1.5}
        onClick={toggleMenu}
      />
      <Panel
        onClose={() => setIsOpen(false)}
        show={isOpen}
      >
        <>
          {!selectedSeason ? (
            <>
              <CompTitle onClick={(e) => {
                e.stopPropagation();
                currentSeason && setSelectedSeason(currentSeason);
              }}>
                <Text variant="body2" mx="auto">{__t('player.choose_season')}</Text>
                <Icon size="semiSmall" cursor="pointer">
                  <IconRightArrow />
                </Icon>
              </CompTitle>

              <Divider mt="0.5rem" mb="1.25rem" />

              <ScrollableList>
                {contentData?.seasons.map((season, index) => (
                  <SeasonItem key={`${season.id}-${index}`} onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSeason(season);
                  }}>
                    <Flex justify="space-between" align="center">
                      <Icon size="semiSmall">
                        <IconLeftArrow />
                      </Icon>
                      <Text variant="body2">
                        {__t('player.season_id', { id: season.id })}
                      </Text>
                    </Flex>
                  </SeasonItem>
                ))}
              </ScrollableList>
            </>
          ) : (
            <>
              {contentData?.seasons && contentData?.seasons.length > 1 ? <SeasonTitleWrapper
                onClick={(e) => {
                  e.stopPropagation();
                  contentData?.seasons && contentData?.seasons.length > 1 && setSelectedSeason(undefined);
                }}>
                <Flex justify="space-between" align="center">
                  {contentData?.seasons && contentData?.seasons.length > 1 && <Icon size="semiSmall">
                    <IconLeftArrow />
                  </Icon>}
                  <Text variant="body2">
                    {__t('player.season_id', { id: selectedSeason?.id })}
                  </Text>
                </Flex>
              </SeasonTitleWrapper> : <SeasonTitleTextWrapper>
                <Text variant="body2">
                  {__t('player.season_id', { id: selectedSeason?.id })}
                </Text>
              </SeasonTitleTextWrapper>}

              <ScrollableList>
                {selectedSeason?.episodes.map((episode, index) => (
                  <EpisodeItem
                    key={`${episode.id}-${index}`}
                    isActive={episode.id === selectedEpisode && contentData?.content.season === selectedSeason.id}
                    onMouseEnter={() => {
                      if (contentData?.content.season !== selectedSeason.id) {
                        setHoveredEpisode(episode.id);
                      } else if (!episode.coming_soon && episode.id !== selectedEpisode) {
                        setHoveredEpisode(episode.id);
                      }
                    }}
                    onMouseLeave={() => setHoveredEpisode(null)}
                    onClick={() => {
                      if (!episode.coming_soon && router.asPath !== episode.url) {
                        router.replace(episode.url).then(() => {
                          setPlayerGlobalState({
                            contentConfigs: null,
                            contentData: null,
                            sources: null,
                            vttData: null,
                            captionState: {
                              prevCaptionIndex: -1,
                              selectedCaptionLang: {
                                lang: 'off',
                                label: 'خاموش',
                              },
                            },
                            showingNextEpisode: true,
                          });
                        });
                      }
                    }}
                  >
                    <Flex justify="flex-end" align="center">
                      <Box>
                        <Flex justify="flex-end" mb="0.5rem">
                          {episode?.id <= 0 && episode.subtitle ? <Text variant="body2">{episode.subtitle}</Text> : <>
                            <Text variant="body2" mr="0.25rem">{episode.id}</Text>
                            <Text variant="body2">{__t('player.episode')}</Text>
                          </>}
                        </Flex>

                        {!!(!episode.coming_soon && episode.duration) && <TimeWrapper show={!!(!episode.coming_soon && episode.duration)} justify="flex-end">
                          <Text color="weaker" variant="body2">
                            {__t('player.minutes')}
                          </Text>

                          <Text color="weaker" variant="body2" ml="0.25rem">
                            {timeToMinuets(episode.duration)}
                          </Text>
                        </TimeWrapper>}

                        {episode.coming_soon && <Flex justify="flex-end">
                          <SeriesBadge>
                            <Text color="black" variant="body3">{__t('flag.coming_soon')}</Text>
                          </SeriesBadge>
                        </Flex>}
                      </Box>

                      <SeriesImageWrapper>
                        <SeriesImage
                          alt={episode.title}
                          src={episode.cover_image}
                        />

                        {(episode.id === selectedEpisode) && (contentData?.content.season === selectedSeason.id) &&
                          <SeriesIconWrapper>
                            <Icon size="small">
                              <IconPause />
                            </Icon>
                          </SeriesIconWrapper>}

                        {(episode.id === hoveredEpisode) && <SeriesIconWrapper>
                          <Icon size="small">
                            <IconPlayBig />
                          </Icon>
                        </SeriesIconWrapper>}
                      </SeriesImageWrapper>
                    </Flex>
                  </EpisodeItem>
                ))}
              </ScrollableList>
            </>
          )}
        </>
      </Panel>
    </>
  );
};

export const TimeWrapper = styled(Flex)<{ show: boolean }>(({ show }) => {
  return {
    visibility: show ? 'visible' : 'hidden',
  };
});

export const Subtitle = styled(Text)(() => {
  return {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '7.5rem',
    direction: 'rtl',
  };
});

export default SeasonsAndEpisodes;
